import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Separator,
} from "@/components/ui/";
import { activeCommandAtom } from "@/features/Charting/state/atoms"; // Adjust import path as needed
import { useNavigate } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";

type CommandPage = "root" | "navigateToPatientInput";

export const QuickCommand = () => {
  const [open, setOpen] = useState(false);
  const setActiveCommand = useSetAtom(activeCommandAtom);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState<CommandPage>("root");
  const [inputValue, setInputValue] = useState(""); // For general input, reused for patient ID

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "j" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prevOpen) => !prevOpen);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Reset to root page when dialog closes or opens
  useEffect(() => {
    if (open) {
      setCurrentPage("root");
      setInputValue("");
    }
  }, [open]);

  const handleSelectAddMedication = () => {
    setActiveCommand({
      id: `cmd_${Date.now()}`,
      type: "addMedication",
      handled: false,
    });
    setOpen(false); // Close the dialog after selection
  };

  const handleSelectNavigateToPatient = () => {
    setCurrentPage("navigateToPatientInput");
    setInputValue(""); // Clear input for patient ID entry
  };

  const handlePatientIdSubmit = useCallback(() => {
    if (inputValue.trim()) {
      navigate({
        to: "/patient/$patientId",
        params: { patientId: inputValue.trim() },
        search: {
          encounterId: undefined,
        },
      });
      setOpen(false);
    }
  }, [inputValue, navigate]);

  const renderPage = () => {
    if (currentPage === "navigateToPatientInput") {
      return (
        <>
          <CommandInput
            placeholder="Enter Patient ID..."
            value={inputValue}
            className="h-4"
            onValueChange={setInputValue}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handlePatientIdSubmit();
              }
            }}
          />
          <CommandList>
            <CommandEmpty noResultsText="Type a Patient ID and press Enter." />
            {inputValue.trim() && (
              <CommandItem
                onSelect={handlePatientIdSubmit}
                value={`navigate-to-${inputValue.trim()}`} // Ensure unique value for selection
              >
                Navigate to Patient: {inputValue.trim()}
              </CommandItem>
            )}
          </CommandList>
        </>
      );
    }

    // Root page
    return (
      <>
        <CommandInput
          placeholder="Type a command or search..."
          className="h-4"
          value={inputValue}
          onValueChange={setInputValue}
        />
        <CommandList>
          <CommandEmpty noResultsText="No results found." />
          <CommandGroup heading="Charting" className="border-none">
            <CommandItem onSelect={handleSelectAddMedication}>
              Add Medication
            </CommandItem>
            {/* Add other actions like Add EOC here */}
          </CommandGroup>
          <Separator />
          <CommandGroup heading="Navigation" className="border-none">
            <CommandItem
              onSelect={handleSelectNavigateToPatient}
              value="navigate-to-patient-input-trigger"
            >
              Navigate to Patient
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </>
    );
  };

  return (
    // When onOpenChange is called and open becomes false, reset state
    <CommandDialog
      open={open}
      onOpenChange={(newOpenState) => {
        setOpen(newOpenState);
        if (!newOpenState) {
          setCurrentPage("root");
          setInputValue("");
        }
      }}
    >
      {renderPage()}
    </CommandDialog>
  );
};
